import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
    catchError,
    combineLatest,
    EMPTY,
    exhaustMap,
    map, mergeMap,
    of,
    switchMap,
    tap,
    throwError,
    withLatestFrom,
} from 'rxjs';
import { v4 } from 'uuid';
import { ModalWindowsService } from '../../../modals/services/modal-windows.service';
import { SnackBarType } from '../../../shared/components/snack-bar/snack-bar.component';
import {
    MAX_GAME_ACCOUNTS_PER_GAME,
    NOTIFICATIONS,
} from '../../../shared/constants';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import { NotificationsService } from '../../../shared/services/notifications.service';
import { TournamentParticipantService } from '../../../tournament/services/tournament-participants.service';
import { ChangePlayerProfileRequest } from '../../models/api/requests/profileGeneral.request';
import {
    AddGameAccountResponse,
    ChangePlayerProfileResponse,
    ProfileStatistics, ReferralsResponse,
} from '../../models/api/responses/profileGeneral.response';
import { Invitation } from '../../models/api/responses/profileInvitations.response';
import { Player, Profile } from '../../models/currentPlayer.interface';
import {
    InvitationExternalIdType,
    InvitationType,
} from '../../models/enums/profile.enum';
import { InvitationService } from '../../services/invitation.service';
import { ProfileGeneralService } from '../../services/profile-general.service';
import { ProfileGeneralActions } from '../actions/profileGeneral.action';
import * as ProfileGeneralSelectors from '../selectors/profileGeneral.selector';
import { Router } from '@angular/router';


@Injectable()
export class ProfileGeneralEffect {
    private actions$ = inject(Actions);
    private store = inject(Store);
    private notificationsService = inject(NotificationsService);
    private tournamentParticipantsService = inject(TournamentParticipantService);
    private profileGeneralService = inject(ProfileGeneralService);
    private invitationService = inject(InvitationService);
    private modalService = inject(ModalWindowsService);
    private router = inject(Router);

    fetchCurrentPlayerData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileGeneralActions.fetchCurrentPlayerData),
            switchMap(() =>
                this.profileGeneralService.getCurrentPlayerData().pipe(
                    map((player: Profile) =>
                        ProfileGeneralActions.fetchCurrentPlayerDataSuccess({
                            payload: player,
                        })
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            ProfileGeneralActions.fetchCurrentPlayerDataFailure(
                                { error: response }
                            )
                        )
                    )
                )
            )
        )
    );

    fetchPlayerData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileGeneralActions.fetchPlayerData),
            switchMap((action) =>
                this.profileGeneralService
                    .getPlayerData(action.payload.playerId)
                    .pipe(
                        map((player: Player) =>
                            ProfileGeneralActions.fetchPlayerDataSuccess({
                                payload: player,
                            })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileGeneralActions.fetchPlayerDataFailure({
                                    error: response,
                                })
                            )
                        )
                    )
            )
        )
    );
    fetchPlayerRefData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileGeneralActions.fetchPlayerRefData),
            switchMap((action) =>
                this.profileGeneralService
                    .getPlayerRefData(action.payload.influencerProfileId)
                    .pipe(
                        map((stats: ReferralsResponse[]) =>
                            ProfileGeneralActions.fetchPlayerRefDataSuccess({
                                payload: stats,
                            })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileGeneralActions.fetchPlayerRefDataFailure({
                                    error: response,
                                })
                            )
                        )
                    )
            )
        )
    );

    saveUserProfileInformation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileGeneralActions.changePlayerProfileInformation),
            switchMap((action) => {
                const requestId = v4();

                const body: ChangePlayerProfileRequest = {
                    ...action.payload,
                    requestId,
                };

                return this.profileGeneralService
                    .changePlayerProfileData(body)
                    .pipe(
                        map((response: ChangePlayerProfileResponse) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.PROFILE_INFORMATION_UPDATED,
                                type: SnackBarType.Success,
                            });

                            return ProfileGeneralActions.changePlayerProfileInformationSuccess(
                                {
                                    payload: response,
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            if (!response.error.failures) {
                                this.notificationsService.openSnackBar({
                                    title: 'Updating error',
                                    message: response.error.message,
                                    type: SnackBarType.Error,
                                    error: response.error,
                                });
                            }

                            return of(
                                ProfileGeneralActions.changePlayerProfileInformationFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    addAccount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileGeneralActions.addGameAccount),
            withLatestFrom(
                this.store.select(ProfileGeneralSelectors.selectCurrentPlayer)
            ),
            exhaustMap(([action, currentPlayer]) => {
                const {
                    profile: { playerId, gameAccounts },
                } = currentPlayer;

                const gameAccountsByGame = gameAccounts.filter(
                    (a) =>
                        a.platform === action.payload.gamingPlatform &&
                        a.gameId === action.payload.gameId
                );

                if (gameAccountsByGame.length >= MAX_GAME_ACCOUNTS_PER_GAME) {
                    this.notificationsService.openSnackBar({
                        title: 'Add new account error',
                        message: NOTIFICATIONS.ADD_GAME_ACCOUNT_FAILURE,
                        type: SnackBarType.Error,
                    });
                    return of(
                        ProfileGeneralActions.addGameAccountFailure({
                            error: null,
                        })
                    );
                }

                return this.profileGeneralService
                    .addGameAccount({
                        ...action.payload,
                        playerProfileId: playerId,
                    })
                    .pipe(
                        map((response: AddGameAccountResponse) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.GAME_ACCOUNT_ADDED,
                                type: SnackBarType.Success,
                            });

                            return ProfileGeneralActions.fetchCurrentPlayerData();
                        }),
                        catchError((response: BadResponseWithError) => {
                            const message = response.error.message;
                            this.notificationsService.openSnackBar({
                                title: message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });
                            return of(
                                ProfileGeneralActions.addGameAccountFailure({
                                    error: response,
                                })
                            );
                        })
                    );
            })
        )
    );

    closeAddAccountDialog$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProfileGeneralActions.addGameAccountFailure),
                tap(() => {
                    this.store.dispatch(
                        ProfileGeneralActions.closeAddGameAccountModal()
                    );
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    deleteAccount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileGeneralActions.deleteGameAccount),
            switchMap(({ payload }) =>
                this.profileGeneralService.deleteGameAccount(payload.id).pipe(
                    map((response: AddGameAccountResponse) => {
                        this.notificationsService.openSnackBar({
                            title: NOTIFICATIONS.GAME_ACCOUNT_DELETED,
                            type: SnackBarType.Success,
                        });
                        return ProfileGeneralActions.deleteGameAccountSuccess({
                            payload: response,
                        });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationsService.openSnackBar({
                            title: response.error.message,
                            type: SnackBarType.Error,
                            error: response.error,
                        });

                        return of(
                            ProfileGeneralActions.addGameAccountFailure({
                                error: response,
                            })
                        );
                    })
                )
            )
        )
    );

    openModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProfileGeneralActions.openAddGameAccountModal),
                tap(() => {
                    this.modalService.openAddGameModal();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    closeModal = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProfileGeneralActions.closeAddGameAccountModal),
                tap(() => {
                    this.modalService.closeAddGameModal();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    fetchInvitationByTargetId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileGeneralActions.fetchInvitationByTargetId),
            switchMap(({ payload }) =>
                this.invitationService
                    .getAllProfileInvitations(payload.profileId)
                    .pipe(
                        map((response: Invitation[]) =>
                            response.find(
                                (i) =>
                                    i.targetKeys.some(
                                        (k) => k.externalId === payload.targetId
                                    ) && !i.isApplication
                            )
                        ),
                        switchMap((invitation: Invitation | undefined) =>
                            invitation
                                ? combineLatest([
                                      of(invitation),
                                      this.profileGeneralService.getBaseInfo(
                                          invitation?.sentBy ?? ''
                                      ),
                                      this.getAdditionalRequest(invitation),
                                  ])
                                : throwError(
                                      () => new Error('not found invitation')
                                  )
                        ),
                        map(([invitation, senderProfileInfo, additionalInfo]) =>
                            ProfileGeneralActions.fetchInvitationByTargetIdSuccess(
                                {
                                    payload: {
                                        ...invitation,
                                        sentByInfo: senderProfileInfo,
                                        additionalInfo,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileGeneralActions.fetchInvitationByTargetIdFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchProfileStatistics = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileGeneralActions.fetchProfileStatistics),
            switchMap(({ payload }) =>
                this.profileGeneralService
                    .fetchProfileStatistics(
                        payload.playerId,
                        payload.gameId,
                        payload.platform
                    )
                    .pipe(
                        map((response: ProfileStatistics) =>
                            ProfileGeneralActions.fetchProfileStatisticsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                ProfileGeneralActions.fetchProfileStatisticsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    fetchPlayerSubscriptions = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileGeneralActions.fetchSubscriptionRequest),
            switchMap((payload: any) =>
                {
                    return this.profileGeneralService
                        .fetchPlayerSubscriptions(
                            payload.payload.query,
                            payload.payload.profileId
                        )
                        .pipe(
                            map((response: ProfileStatistics) =>
                                ProfileGeneralActions.fetchSubscriptionRequestSuccess(
                                    {
                                        payload: response,
                                    }
                                )
                            ),
                            catchError((response: BadResponseWithError) => {
                                this.notificationsService.openSnackBar({
                                    title: response.error?.message,
                                    type: SnackBarType.Error,
                                    error: response.error,
                                });

                                return of(
                                    ProfileGeneralActions.fetchSubscriptionRequestFailure(
                                        {
                                            payload: response,
                                        }
                                    )
                                );
                            })
                        )
                }
            )
        )
    );



    upgradePlayerSubscription = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileGeneralActions.upgradeSubscriptionRequest),
            switchMap((payload) =>
                this.profileGeneralService
                    .upgradeSubscription(
                        payload.payload
                    )
                    .pipe(
                        mergeMap((response: any) =>{
                            this.modalService.closeBuyEarlyPassModal();
                            const url  = payload.payload.returnPath;
                            let decodedUrl = url;
                            if (url.includes('%3') || url.includes('%3D')) {
                                decodedUrl = url
                                    .replace(/%3D/g, '=')
                                    .replace(/%3/g, '?');
                            }
                            this.router.navigateByUrl(`${decodedUrl}`)
                            return [
                                ProfileGeneralActions.upgradeSubscriptionRequestSuccess(
                                    {
                                        payload: response,
                                    }
                                ),
                            ]
                            }
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                ProfileGeneralActions.upgradeSubscriptionRequestFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    private getAdditionalRequest(invitation: Invitation) {
        switch (invitation.targetType) {
            case InvitationType.TournamentTeam:
                return this.getTournamentTeamInfoByInvitation(invitation);
            default:
                return of(null);
        }
    }

    private getTournamentTeamInfoByInvitation(invitation: Invitation) {
        const participantId = invitation.targetKeys.find(
            (k) => k.externalIdType === InvitationExternalIdType.ParticipantId
        )?.externalId;
        const tournamentId = invitation.targetKeys.find(
            (k) => k.externalIdType === InvitationExternalIdType.TournamentId
        )?.externalId;

        if (participantId && tournamentId) {
            return this.tournamentParticipantsService.getTeamTournamentInfo(
                tournamentId,
                participantId
            );
        } else {
            return of(null);
        }
    }
}
